/*
 * Description:
 * ---------------------
 * The Home Wi-Fi screen's coverage map: a walk someone made through the home
 * with the Weavian app, as the box read it (cloud-anchor/pkg/homewifi
 * survey.go). The signal is drawn only where the walk went; the access
 * point, the places the person named and, where one would help, the spot
 * for an extender sit on it. All wording lives in homeWifiWords.js.
 *
 * ! file components/HomeWifiMap.js
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { nodeApiUrlFor } from "../serverApi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import * as W from "./homeWifiWords";

async function getJSON(url) {
  const res = await fetchWithTimeout(url).catch(() => null);
  return res?.ok ? res.json().catch(() => null) : null;
}

export default function HomeWifiMap({ selectedNode }) {
  const url = useMemo(() => nodeApiUrlFor(selectedNode), [selectedNode]);
  const [list, setList] = useState(null);
  const [pick, setPick] = useState("");
  const [analysis, setAnalysis] = useState(null);

  const readList = useCallback(async () => {
    const body = await getJSON(url("/wifi/home/surveys"));
    setList(body?.surveys || []);
  }, [url]);
  useEffect(() => {
    readList();
  }, [readList]);
  useVisibleInterval(readList, 30000);

  const id = pick || list?.[0]?.id || "";
  useEffect(() => {
    if (!id) return;
    let live = true;
    getJSON(url(`/wifi/home/surveys/${encodeURIComponent(id)}`)).then((a) => live && setAnalysis(a));
    return () => {
      live = false;
    };
  }, [url, id]);

  if (list === null) return null;
  return (
    <div className="panel">
      <div className="dl-head">
        <div className="panel-title">Coverage map</div>
        {list.length > 1 && (
          <select className="dl-input hw-walk" value={id} onChange={(e) => setPick(e.target.value)} aria-label="Which walk">
            {list.map((s) => (
              <option key={s.id} value={s.id}>{walkLabel(s)}</option>
            ))}
          </select>
        )}
      </div>
      {list.length === 0 && <p className="dl-note">{W.MAP_NONE}</p>}
      {list.length > 0 && analysis && analysis.id === id && <Map a={analysis} />}
    </div>
  );
}

function walkLabel(s) {
  const t = new Date(s.at);
  const when = Number.isNaN(t.getTime()) ? s.id : t.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  return `${when}${s.by ? `, ${s.by}` : ""}`;
}

const FILL = { strong: "#16A34A", good: "#86EFAC", fair: "#FBBF24", weak: "#EF4444" };

function Map({ a }) {
  const w = a.cols * a.cell_m;
  const h = a.rows * a.cell_m;
  // Metres to the drawing: y grows up in the walk, down on the screen.
  const sx = (x) => x - a.min_x;
  const sy = (y) => h - (y - a.min_y);
  const units = [...new Set((a.aps || []).map((ap) => ap.unit))];
  const unitAt = (u) => (a.aps || []).find((ap) => ap.unit === u);
  const path = (a.path || []).map((p) => `${sx(p.x).toFixed(2)},${sy(p.y).toFixed(2)}`).join(" ");
  // Labels keep one size on the screen whatever the home's size.
  const label = { fontSize: w / 48, letterSpacing: 0 };
  return (
    <div className="hw-map">
      <p className="hw-why">{W.mapHeadline(a)}</p>
      <svg viewBox={`0 0 ${w} ${h}`} className="hw-map-svg" role="img" aria-label="The home's Wi-Fi signal where the walk went">
        {(a.cells || []).map((v, i) => {
          if (v == null) return null;
          const c = i % a.cols;
          const r = Math.floor(i / a.cols);
          const x = a.min_x + c * a.cell_m;
          const y = a.min_y + r * a.cell_m;
          return (
            <rect key={i} x={sx(x) - a.cell_m / 2} y={sy(y) - a.cell_m / 2} width={a.cell_m} height={a.cell_m}
              fill={FILL[W.signalTone(v)]} opacity="0.55" />
          );
        })}
        {path && <polyline points={path} fill="none" stroke="#334155" strokeWidth={a.cell_m * 0.12} strokeLinejoin="round" opacity="0.7" />}
        {units.map((u, i) => {
          const ap = unitAt(u);
          return (
            <g key={u}>
              <circle cx={sx(ap.x)} cy={sy(ap.y)} r={a.cell_m * 0.7} fill="#1E293B" />
              <text x={sx(ap.x)} y={sy(ap.y) - a.cell_m * 1.1} className="hw-map-label" style={label} textAnchor="middle">
                {i === 0 ? "Router" : `Access point ${i + 1}`}
              </text>
            </g>
          );
        })}
        {(a.marks || []).filter((m) => m.kind !== "router").map((m, i) => (
          <g key={i}>
            <rect x={sx(m.x) - a.cell_m * 0.45} y={sy(m.y) - a.cell_m * 0.45} width={a.cell_m * 0.9} height={a.cell_m * 0.9}
              rx={a.cell_m * 0.15} fill="#fff" stroke="#1E293B" strokeWidth={a.cell_m * 0.1} />
            <text x={sx(m.x)} y={sy(m.y) - a.cell_m * 0.9} className="hw-map-label" style={label} textAnchor="middle">{m.label}</text>
          </g>
        ))}
        {a.extender && (
          <g>
            <Star x={sx(a.extender.x)} y={sy(a.extender.y)} r={a.cell_m * 0.9} />
            <text x={sx(a.extender.x)} y={sy(a.extender.y) + a.cell_m * 1.8} className="hw-map-label" style={label} textAnchor="middle">Extender here</text>
          </g>
        )}
      </svg>
      <div className="hw-legend">
        {Object.keys(FILL).map((k) => (
          <span key={k}><i style={{ background: FILL[k] }} />{W.SIGNAL_WORDS[k]}</span>
        ))}
      </div>
      {(a.marks || []).filter((m) => m.kind !== "router").map((m, i) => <p key={i} className="hw-line">{W.markSentence(m)}</p>)}
      {a.extender && <p className="hw-why">{W.extenderSentence(a.extender)}</p>}
      <details className="dl-measure">
        <summary>How the box knows</summary>
        {(a.aps || []).map((ap) => (
          <span key={ap.bssid}>
            {ap.band || "Network"} {ap.bssid}: placed from {ap.samples} readings{ap.anchored ? ", the walk having begun beside it" : ""};
            {" "}{Math.round(ap.a_dbm)} dBm a metre away, falling with an exponent of {ap.n}; readings stray {ap.fit_db} dB from that on average.
          </span>
        ))}
        <span>Each cell is {a.cell_m} m. The signal is drawn only within {a.reach_m} m of where the walk went.</span>
      </details>
    </div>
  );
}

function Star({ x, y, r }) {
  const pts = [];
  for (let i = 0; i < 10; i++) {
    const rad = (Math.PI / 5) * i - Math.PI / 2;
    const rr = i % 2 === 0 ? r : r * 0.45;
    pts.push(`${(x + rr * Math.cos(rad)).toFixed(2)},${(y + rr * Math.sin(rad)).toFixed(2)}`);
  }
  return <polygon points={pts.join(" ")} fill="#4F46E5" stroke="#fff" strokeWidth={r * 0.12} />;
}
